<template>
    <div class="Loops pr-1 pl-1 pt-1">
        <div class="q-mb-sm">
            <QBtnToggle
                v-model="tab"
                outline
                color="primary"
                toggleColor="primary"
                noCaps
                unelevated
                :options="loopTabOptions"
                class="full-width ContextDrawerToggle"
                @update:modelValue="handleChangeTab"
            />
        </div>

        <div class="flex row-stretch-center flex-nowrap">
            <span class="text-primary text-subtitle2">
                {{ $t('visualization.loops') }}
            </span>
            <QInput
                v-model="search"
                dense
                outlined
                type="search"
                :label="$t('actions.search')"
                :disable="disableElements"
                class="q-ml-sm"
                @keyup.enter="handleSearchLoops(search)"
            >
                <template #append>
                    <q-icon
                        v-if="search"
                        id="clean"
                        name="close"
                        size="20px"
                        clickable
                        style="margin-right: 0.5rem;cursor: pointer;"
                        @click="handleResetSearch"
                    />
                    <q-icon
                        id="search"
                        name="search1"
                        size="20px"
                        clickable
                        style="cursor: pointer;"
                        @click="handleSearchLoops(search)"
                    />
                </template>
            </QInput>
        </div>

        <QTabPanels v-model="tab">
            <QTabPanel
                class="model-tab-panel"
                :name="tab"
            >
                <div class="scroll">
                    <q-inner-loading :showing="isLoading" />
                    <div v-show="showLoops">
                        <a
                            v-for="(item) in loops"
                            :key="item.id"
                            href="#"
                            class="Bar mt-0-5"
                            :aria-disabled="disableElements ? 'true' : 'false'"
                            :class="{ selected: item.id === activeLoop, disabled: disableElements }"
                            @click.prevent="handleChangeLoop(item.id, item.name)"
                        >
                            <div class="flex row-stretch-center">
                                <WText
                                    weight="semi"
                                    :size="12"
                                >
                                    {{ item.name.replace('Loop', $t('visualization.loop')) }}
                                    <QBtn
                                        v-if="activeLoop === item.id"
                                        flat
                                        square
                                        icon="edit"
                                        size="10px"
                                        color="white"
                                        textColor="primary"
                                        class="q-mr-sm"
                                        padding="0px"
                                        :style="{ marginLeft: '5px' }"
                                        @click.stop="handleRenameLoop(item.id)"
                                    />
                                </WText>
                                <WText
                                    weight="semi"
                                    :size="12"
                                >
                                    {{ App.numberLocationFormat(getAbsFrequency(item.absFrequency) * 100) }}%
                                </WText>
                            </div>
                            <QLinearProgress
                                size="10px"
                                :value="getAbsFrequency(item.absFrequency)"
                                class="LinearProgress"
                            />
                            <QTooltip
                                class="Tooltip text-primary"
                                maxWidth="500px"
                                style="font-size:13px; background-color:white; border: solid 1px; border-radius: 5px;"
                                anchor="center right"
                                self="center left"
                            >
                                <WText
                                    color="primary"
                                >
                                    <strong>
                                        {{ item.name.replace('Loop', $t('visualization.loop')) }}
                                    </strong>: {{ $t('visualization.loopsFound') }} {{ item.absFrequency }} {{ $t('visualization.cases') }}
                                    ({{ percentage(getAbsFrequency(item.absFrequency)) }}%)
                                </WText>
                            </QTooltip>
                        </a>
                    </div>
                    <div
                        v-if="!isLoading && !showLoops"
                        class="flex col-center mt-2"
                    >
                        <Badge
                            icon="warning_amber"
                            :label="$t('visualization.notifications.notFound.text', { type: $t('visualization.loops') })"
                            color="warning"
                            textColor="black"
                            class="row-center pt-0-5 pb-0-5 mb-0-5"
                        />
                    </div>
                </div>
                <div
                    v-if="showLoops"
                    class="flex row-center pt-1"
                >
                    <QPagination
                        v-model="pagination.start"
                        :max="pagination.max"
                        :directionLinks="true"
                        :maxPages="4"
                        :disable="disableElements"
                        size="11.5px"
                        :boundaryNumbers="true"
                        @update:modelValue="handleChangePagination"
                    />
                </div>
                <div class="mt-1">
                    <QBtn
                        outline
                        color="primary"
                        noCaps
                        dense
                        padding="xs"
                        class="w-full"
                        style="justify-content: center;"
                        :disable="loopFilterExists || disableElements || emptyLoops || !showLoops"
                        :class="{ 'active-filter': loopFilterExists }"
                        @click="filterByLoop"
                    >
                        <QIcon
                            name="filter_alt"
                            left
                            size="15px"
                        />
                        {{ loopFilterExists ? $t('visualization.filterLoopApplied') : $t('visualization.filterLoop') }}
                    </QBtn>
                </div>
            </QTabPanel>
        </QTabPanels>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import {
    Api, apiRequest, notifyError, notifySuccess,
} from '@/api'
import { Badge } from '@/components'
import {
    LOOP_FILTERS,
} from '@/screens/Project/Process/Filters/FilterModal/filtersEntity'
import { filtersStorageMixin, filtersMixin } from '@/mixins'

const INITIAL_LOOPS_PAGINATION = { start: 0, limit: 8 }

const TABS = {
    LOOPS: 'loops',
    SELF_LOOPS: 'self-loops',
}

export default {
    name: 'LoopsPanel',
    components: {
        Badge,
    },
    mixins: [filtersStorageMixin, filtersMixin],
    inject: ['App'],
    props: {
        value: VueTypes.shape({
        }).loose,
        info: VueTypes.object,
        // filteredInfo: VueTypes.object,
        percentageMode: VueTypes.string,
        processId: VueTypes.oneOfType([VueTypes.string, VueTypes.number]),
        disableElements: VueTypes.bool.def(false),
    },
    emits: ['onOpenRenameLoopModal', 'checkForVariantFilter', 'filterByLoop', 'input', 'variantsLoading', 'onLoadChart', 'onRequestModelInfo'],
    data() {
        return {
            activeLoop: undefined,
            loopName: undefined,
            loops: undefined,
            isLoading: undefined,
            isFetching: undefined,
            search: '',
            pagination: {
                start: 1,
                max: INITIAL_LOOPS_PAGINATION.limit,
            },
            tab: TABS.LOOPS,
            loopvariants: undefined,
            emptyLoops: false,
        }
    },
    computed: {
        showLoops() {
            return !this.isLoading && (this.loops || []).length
        },
        loopTabOptions() {
            return [
                { value: TABS.LOOPS, label: this.$t('visualization.nLoops'), icon: '360' },
                { value: TABS.SELF_LOOPS, label: this.$t('visualization.selfLoops'), icon: 'repeat_one' },
            ]
        },
        loopFilterExists() {
            const { visualizationFilters } = this
            const loopFilter = (visualizationFilters || []).find((filter) => (LOOP_FILTERS.includes(filter.kind) || LOOP_FILTERS.includes(filter.id)))
            return !!loopFilter
        },
    },
    watch: {
        isFetching: 'emitVariantsLoading',
        isLoading: 'emitVariantsLoading',
    },
    mounted() {
        this.initializeLoops()
    },
    methods: {
        initializeLoops() {
            if (this.processId) this.getLoops()
        },
        handleRenameLoop(loopId) {
            this.$emit('onOpenRenameLoopModal', { loopId })
        },
        filterByLoop() {
            this.$eventBus.emit('filterByLoop', { variant: [this.activeLoop], loopName: this.loopName })
        },
        handleDirectionChange(value) {
            this.directionValue = value
            this.handleInput('directionValue', { value: value ? 'LR' : 'TB' })
        },
        handleInput(key, { value }) {
            this.$emit('input', { ...(this.value || {}), [key]: value })
        },
        handleChangeTab(tab) {
            this.pagination.start = 1
            this.tab = tab
            this.getLoops()
        },
        handleChangeLoop(id, name) {
            this.activeLoop = id
            this.loopName = name
            this.getLoop(id)
        },
        normalizeLoops(loops) {
            return (loops || []).map((loop) => ({ ...loop, value: loop.relativeFrequency }))
        },
        async renameLoop({ newName, loopRenamedId }) {
            this.isVariantsLoading = true
            const { processId } = this
            const loopId = this.loops.find((v) => v.id === loopRenamedId).id
            const params = { name: newName }
            apiRequest(Api().visualizations.renameLoop({ processId, loopId, params }))
                .then(() => {
                    this.handleResetSearch()
                    notifySuccess(this.$t('visualization.loopRenameOK'))
                })
                .catch((e) => {
                    notifyError(e)
                })
                .finally(() => { this.isVariantsLoading = false })
        },
        async getLoops(param) {
            const self = this.tab === TABS.SELF_LOOPS
            this.isLoading = true
            const params = this.buildVisualizationParams(this.normalizeParams(param, self))
            apiRequest(Api().visualizations.loops({ processId: this.processId, params }))
                .then(({ data, total }) => {
                    const [first] = data
                    this.loops = this.normalizeLoops(data)
                    if (data.length === 0) {
                        const emptyLoop = {
                            name: null,
                            activities: [],
                            arcs: [],
                            frequency: 0,
                            duration: {
                                avg: 0,
                                sd: 0,
                                max: 0,
                                min: 0,
                                med: 0,
                            },
                        }
                        this.$emit('onRequestModelInfo', { loopId: undefined })
                        this.$emit('onLoadChart', { ...emptyLoop })
                    } else {
                        this.pagination.max = this.normalizePaginationMax(total)
                        if (first) this.handleChangeLoop(first.id, first.name)
                    }
                })
                .catch(notifyError)
                .finally(() => {
                    this.isLoading = false
                })
        },
        normalizePaginationMax(value) {
            const max = Math.round(value / INITIAL_LOOPS_PAGINATION.limit)
            return max || 1
        },
        handleChangePagination(start) {
            this.getLoops({ start: start - 1, search: this.search || '' })
        },
        getAbsFrequency(absFrequency) {
            const cases = this.info?.cases
            return cases ? absFrequency / cases : 0
        },
        normalizeParams(param, self) {
            return {
                ...INITIAL_LOOPS_PAGINATION,
                start: this.pagination.start - 1,
                ...(self ? { self } : {}),
                ...(typeof param === 'string' ? {} : param),
            }
        },
        handleResetSearch() {
            this.search = ''
            this.pagination.start = 1
            this.getLoops({ search: this.search, start: INITIAL_LOOPS_PAGINATION.start })
        },
        handleSearchLoops(search) {
            this.pagination.start = 1
            this.getLoops({ search, start: INITIAL_LOOPS_PAGINATION.start })
        },
        emitVariantsLoading() {
            if (!this.isFetching && !this.isLoading) {
                this.$emit('variantsLoading', false)
            } else {
                this.$emit('variantsLoading', true)
            }
        },
        async getLoop(loopId) {
            this.isFetching = true
            const params = this.buildVisualizationParams({})
            this.$emit('onRequestModelInfo', { loopId })
            apiRequest(Api().visualizations.loop({ processId: this.processId, loopId, params }))
                .then(({ data }) => {
                    const [variant] = data || []
                    this.loopvariants = variant.compliantVariants
                    this.$emit('onLoadChart', { ...variant, loopId })
                })
                .catch(notifyError)
                .finally(() => {
                    this.isFetching = false
                })
        },
    },
}
</script>
<style scoped lang="scss">
.scroll {
    width: 100%;
    overflow-x: hidden;
    overflow-y: auto;
}

.Tabs {
    margin-top: 10px;
}

.Bar {
    display: block;

    &:hover, &.selected {
        .WText { color: $black; }
        .LinearProgress { color: $accent; }
    }

    .WText {
        color: $gray-02;
        margin-bottom: 5px;
    }

    .LinearProgress {
        color: $secondary;
        border-radius: $border-radius;
        transition: $transition;
    }
}

.Stats{
    flex-wrap: nowrap;
}
.Tabs {
    margin-bottom: 15px;
    &:deep() {
        .Container {
            padding: 0;
        }
        .Tab {
            min-height: auto;
        }
    }
}

:deep(.ContextDrawerToggle .q-btn[aria-pressed="true"]) {
    background: rgba($primary, 0.12) !important;
}

:deep(.ContextDrawerToggle.q-btn-group--outline:before),
:deep(.ContextDrawerToggle .q-btn--outline:before) {
    border-width: 1px !important;
}
</style>
